import { FormSchema } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { InspectionTemplate } from '@sw-sw/lib-inspection-templates';
import { find } from 'lodash';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { State } from '../../../utils/api/address';
import { Client } from '../../../utils/api/client';
import validator from '../../../utils/FormValidator';
import { env } from '../../../config';

const halfWidthStyle = { flex: '1 1 50%', alignSelf: 'flex-end' };
const emptyDescriptionStyle = { marginTop: '1.5em' };

export const projectInspectorUserRoles = [
  'Admin',
  'Inspector',
  'Area / Assistant Manager',
  'Regional Manager',
  'SWMP Admin',
  'CDOT PE/Designee',
  'Supervising Engineer',
  'Client Inspector',
  'Client Manager',
  'Regional Manager (External)',
];

const systemInspectionTempate = [
  'CDOT',
  'CDOT 1176 06/2024',
  'CDOT-1177',
  'CDOT 1177 - 5/2024',
  'Colorado',
  'Delaware',
  'EVP_DUMMY_TEMPLATE',
  'Lennar (Colorado)',
  'Maryland',
  'NCDOT',
  'NCDEQ',
  'Xcel',
];

export function getSchemaLeft(
  clients: Client[],
  inspectionTemplates: InspectionTemplate[],
  states: State[],
  users: any[],
  projectTypes: any[],
  timezones: string[],
  disableClient: boolean = false,
  selectedTemplateId: number | null = null,
  inspectionComplianceRequired: boolean = true,
  optionalComplianceTemplates: string[] = [],
  ccrTemplates: string[] = [],
  project_client: Client,
  precipitation_edit_lock: boolean,
  evp: boolean,
  precipitation_flag_tenant_feature: boolean,
) {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    clientId: {
      controlType: UIControlType.select,
      label: 'Client*',
      options: clients,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
      disabled: disableClient,
    },
    permittee: {
      label: 'Permittee',
      description: 'If different than client',
    },
    inspectionTemplateId: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    projectType: {
      controlType: UIControlType.select,
      label: 'Project Type*',
      options: projectTypes,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    isCcrProject: {
      controlType: UIControlType.checkbox,
      label: 'Use CCR Number',
      toolTipOptions: {
        message:
          'If checked the inspector will be required to have a CCR Number that is included on the certification.',
      },
    },
    inspection_compliance_required: {
      controlType: UIControlType.checkbox,
      label: 'Compliance Signature Required',
    },
    findingComplianceRequired: {
      controlType: UIControlType.checkbox,
      label: 'Completed Findings Required For Compliance',
      toolTipOptions: {
        message:
          'When this is selected, compliance signatures are available, even when open or incomplete corrective action findings are present in the inspection.',
      },
    },
    name: {
      label: 'Project Name*',
      validation: { required: true, maxLength: 355 },
      autoComplete: 'none',
    },
    addressStreet: {
      label: 'Project Address',
      placeholder: 'Street',
      className: 'with-spacing',
      'aria-label': 'Street',
      autoComplete: 'address-line1',
    },
    addressCity: {
      placeholder: 'City',
      className: 'with-spacing',
      'aria-label': 'City',
      autoComplete: 'address-level2',
    },
    addressState: {
      controlType: UIControlType.select,
      options: states,
      valueKey: 'id',
      labelKey: env.REACT_APP_REGION === 'UK' ? 'name' : 'abbreviation',
      placeholder:
        env.REACT_APP_REGION === 'UK' ? 'Select Country' : 'Select State',
      autoComplete: 'address-level1',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    addressZip: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Postcode' : 'Zip',
      'aria-label': 'Zip',
      autoComplete: 'postal-code',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
      validation: { format: 'zipcode', maxLength: 5 },
      parse: validator.parseNumber,
    },
    latitude: {
      label: 'Location*',
      placeholder: 'Latitude',
      'aria-label': 'Latitude',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
      // inputMode: "decimal",
      validation: { required: true },
    },
    longitude: {
      placeholder: 'Longitude',
      'aria-label': 'Longitude',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
      // inputMode: "decimal",
      validation: { required: true },
    },
    timezone: {
      controlType: UIControlType.select,
      options:
        env.REACT_APP_REGION === 'UK'
          ? [{ label: 'GMT' }]
          : timezones.map((label) => ({ label })),
      valueKey: 'label',
      labelKey: 'label',
      placeholder: 'Select Timezone',
    },
    inspector_user_id: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
    precipitation_flag: {
      label: 'Precipitation',
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm',
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    precipitation_threshold: {
      label: `Rain Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    snow_threshold: {
      label: `Snow Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
  };

  // the selected template, if any
  const tmpl: InspectionTemplate = find(inspectionTemplates, {
    id: selectedTemplateId || undefined,
  }) || { name: 'unknown', isUserTemplate: false };

  /**
   * CCR Number option is only available for templates
   * in appContext.ccrTemplates
   */
  if (!ccrTemplates.includes(tmpl.name)) {
    delete schema.isCcrProject;
  }

  /**
   * "compliance required" option
   * only display for selected templates
   */
  if (!selectedTemplateId || !optionalComplianceTemplates.includes(tmpl.name)) {
    delete schema.inspection_compliance_required;
  }

  if (
    !selectedTemplateId ||
    !optionalComplianceTemplates.includes(tmpl.name) ||
    (optionalComplianceTemplates.includes(tmpl.name) &&
      !inspectionComplianceRequired)
  ) {
    delete schema.findingComplianceRequired;
  }

  if (evp) {
    delete schema.inspectionTemplateId;
    delete schema.precipitation_flag;
    delete schema.precipitation_threshold;
    delete schema.snow_threshold;
  }

  return schema;
}

export function getSchemaRight(
  docTypes: any[],
  docGroups: any[],
  divisionInspectionIntervals: any[],
): FormSchema {
  return {
    // break this label into it's own, so it doesn't render as half width
    timeLine: {
      controlType: UIControlType.plainText,
      label: 'Anticipated Project Timeline',
      style: { margin: '0' },
    },
    startDate: {
      controlType: UIControlType.date,
      placeholder: 'Start date',
      'aria-label': 'Project start date',
      style: halfWidthStyle,
    },
    endDate: {
      controlType: UIControlType.date,
      placeholder: 'End date',
      'aria-label': 'Project end date',
      style: halfWidthStyle,
      className: 'right-popper',
      // popperPlacement: "top-end",
    },
    next_inspection_date: {
      controlType: UIControlType.date,
      label: `Next Routine Inspection Date`,
      className: 'right-popper',
      // popperPlacement: "top-end",
      validation: {
        minDate: moment()
          .milliseconds(0)
          .seconds(0)
          .minutes(0)
          .hour(0)
          .toDate(),
      },
      style: emptyDescriptionStyle,
    },
    inspection_interval_id: {
      label: 'Routine Inspection Interval*',
      controlType: UIControlType.select,
      placeholder: 'Select Interval...',
      options: divisionInspectionIntervals,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    notes: {
      label: 'Additional Notes',
      controlType: UIControlType.textarea,
    },
    document_group: {
      label: 'Regulations*',
      controlType: UIControlType.select,
      placeholder: 'Select document group...',
      options: docGroups,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    docTypes: {
      label: 'Document Types',
      controlType: UIControlType.checkboxGroup,
      options: docTypes,
      valueKey: 'id',
      labelKey: 'name',
      showSelectAll: true,
    },
  };
}

export function getProjectInformationSchemaLeft(
  clients: Client[],
  inspectionTemplates: InspectionTemplate[],
  states: State[],
  timezones: string[],
  disableClient: boolean = false,
  selectedTemplateId: number | null = null,
  inspectionComplianceRequired: boolean = true,
  optionalComplianceTemplates: string[] = [],
  ccrTemplates: string[] = [],
  evp: boolean,
  clientId: number,
  featureEnableEnhancedFinding: boolean,
  enablePhotoMetadataPermission: boolean,
  enableForecastWeather: boolean,
  enableGuestSignOffs: boolean,
  enableInspectionScheduler: boolean,
  clientInspectionScheduler: boolean,
  clientForecastWeather: boolean,
  forecastWeatherEnabled: boolean,
  areas: any[],
): FormSchema {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    clientId: {
      controlType: UIControlType.select,
      label: 'Client*',
      options: clients,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
      disabled: disableClient,
    },
    permittee: {
      label: 'Permittee (If different than client)',
    },
    inspectionTemplateId: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    isCcrProject: {
      controlType: UIControlType.checkbox,
      label: 'Use CCR Number',
      toolTipOptions: {
        message:
          'If checked the inspector will be required to have a CCR Number that is included on the certification.',
      },
    },
    inspection_compliance_required: {
      controlType: UIControlType.checkbox,
      label: 'Compliance Signature Required',
    },
    findingComplianceRequired: {
      controlType: UIControlType.checkbox,
      label: 'Completed Findings Required For Compliance',
      toolTipOptions: {
        message:
          'When this is selected, compliance signatures are available, even when open or incomplete corrective action findings are present in the inspection.',
      },
    },
    name: {
      label: 'Project Name*',
      validation: { required: true, maxLength: 355 },
      autoComplete: 'none',
    },
    addressStreet: {
      label: 'Project Address',
      placeholder: 'Street',
      className: 'with-spacing',
      'aria-label': 'Street',
      autoComplete: 'address-line1',
    },
    addressCity: {
      placeholder: 'City',
      className: 'with-spacing',
      'aria-label': 'City',
      autoComplete: 'address-level2',
    },
    addressState: {
      controlType: UIControlType.select,
      options: states,
      valueKey: 'id',
      labelKey: env.REACT_APP_REGION === 'UK' ? 'name' : 'abbreviation',
      placeholder:
        env.REACT_APP_REGION === 'UK' ? 'Select Country' : 'Select State',
      autoComplete: 'address-level1',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    addressZip: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Postcode' : 'Zip',
      'aria-label': 'Zip',
      autoComplete: 'postal-code',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
      validation: { format: 'zipcode', maxLength: 5 },
      parse: validator.parseNumber,
    },
    latitude: {
      label: 'Location*',
      placeholder: 'Latitude',
      'aria-label': 'Latitude',
      parse: validator.parseDecimal,
      validation: { required: true },
      style: halfWidthStyle,
    },
    longitude: {
      placeholder: 'Longitude',
      'aria-label': 'Longitude',
      parse: validator.parseDecimal,
      validation: { required: true, format: 'longitude' },
      style: halfWidthStyle,
    },
    timezone: {
      controlType: UIControlType.select,
      options:
        env.REACT_APP_REGION === 'UK'
          ? [{ label: 'GMT' }]
          : timezones.map((label) => ({ label })),
      valueKey: 'label',
      labelKey: 'label',
      placeholder: 'Select Timezone',
    },
    report_type: {
      label: 'Report Type',
      controlType: UIControlType.select,
      options: [
        { id: 'entire_inspection', name: 'Entire Inspection' },
        {
          id: 'entire_inspection_wlocations',
          name: 'Entire Inspection W/Finding Location',
        },
        {
          id: 'entire_inspection_wlocations_wphotos',
          name: 'Entire Inspection W/Finding Location and Photos',
        },
        { id: 'ca_log_wophotos', name: 'Findings Log - Without Photos' },
        { id: 'ca_log_wphotos', name: 'Findings Log - With Photos' },
        { id: 'findings_wphotos', name: 'Open Findings Report - With Photos' },
      ],
      labelKey: 'name',
      valueKey: 'id',
      toolTipOptions: {
        message:
          'Select a report type that will be automatically sent out when an inspection report is completed',
      },
    },
    cgp_tracking_id: {
      label: 'Permit Number',
      style: halfWidthStyle,
    },
    current_acreage: {
      label: 'Current Acreage',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
    },
    work_order: {
      label: 'Work Order #',
      style: halfWidthStyle,
    },
    purchase_order: {
      label: 'Purchase Order #',
      style: halfWidthStyle,
    },
    area_id: {
      label: 'Area',
      controlType: UIControlType.select,
      options: areas,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Select Area',
      style: halfWidthStyle,
    },
    finding_metric_tracking: {
      controlType: UIControlType.checkbox,
      label: 'Enable Enhanced Finding',
      toolTipOptions: {
        message:
          'When enabled, findings may be tied to specific legend items available for use within site map editor. This data will then be used for reporting.',
      },
    },
    dissolve_finding_images: {
      controlType: UIControlType.checkbox,
      label: 'Dissolving Photos',
      toolTipOptions: {
        message:
          '“When enabled, finding photos will be permanently deleted from the system once a date completed has been entered/the finding has been closed” These photos will not be able to be retrieved once they are deleted.',
      },
    },
    enable_photo_metadata_requirement: {
      controlType: UIControlType.checkbox,
      label: 'Enable Photo Metadata Requirement',
    },
    forecast_weather: {
      controlType: UIControlType.checkbox,
      label: 'NOAA Weather Table',
      toolTipOptions: {
        message: `When enabled the NOAA Weather Table will be imported and included on each inspection reprot. This table will display the 7 day forcast in 6 hour intervals.`,
      },
    },
    guest_sign_offs: {
      label: 'Guest Sign-Offs',
      controlType: UIControlType.checkbox,
      style: { display: enableGuestSignOffs ? 'block' : 'none' },
    },
    ca_inspection_scheduler: {
      controlType: UIControlType.checkbox,
      label: 'CA - Inspection Scheduler',
      style: {
        display:
          enableInspectionScheduler &&
          clientInspectionScheduler &&
          clientForecastWeather &&
          forecastWeatherEnabled
            ? 'block'
            : 'none',
      },
      disabled: !clientForecastWeather,
    },
  };
  const tmpl: InspectionTemplate = find(inspectionTemplates, {
    id: selectedTemplateId || undefined,
  }) || { name: 'unknown', isUserTemplate: false };

  if (!ccrTemplates.includes(tmpl.name)) {
    delete schema.isCcrProject;
  }

  if (!selectedTemplateId || !optionalComplianceTemplates.includes(tmpl.name)) {
    delete schema.inspection_compliance_required;
  }

  if (
    !selectedTemplateId ||
    !optionalComplianceTemplates.includes(tmpl.name) ||
    (optionalComplianceTemplates.includes(tmpl.name) &&
      !inspectionComplianceRequired)
  ) {
    delete schema.findingComplianceRequired;
  }

  if (evp) {
    delete schema.inspectionTemplateId;
  }

  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.finding_metric_tracking === false ||
    !featureEnableEnhancedFinding
  ) {
    delete schema.finding_metric_tracking;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.dissolve_finding_images === false
  ) {
    delete schema.dissolve_finding_images;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.enable_photo_metadata_requirement === false ||
    !enablePhotoMetadataPermission
  ) {
    delete schema.enable_photo_metadata_requirement;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.forecast_weather === false ||
    !enableForecastWeather
  ) {
    delete schema.forecast_weather;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.lock_report_type ||
    systemInspectionTempate.includes(tmpl.name)
  ) {
    delete schema.report_type;
  }
  if (
    !enableInspectionScheduler ||
    !clientInspectionScheduler ||
    !clientForecastWeather
  ) {
    delete schema.ca_inspection_scheduler;
  }

  return schema;
}

export function getProjectInformationSchemaRight(
  users: any[],
  primaryContactUsers: any[],
  projectTypes: any[],
  divisionInspectionIntervals: any[],
  docGroups: any[],
): FormSchema {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    timeLine: {
      controlType: UIControlType.plainText,
      label: 'Anticipated Project Timeline',
      style: { margin: '0' },
    },
    startDate: {
      controlType: UIControlType.date,
      placeholder: 'Start date',
      'aria-label': 'Project start date',
      style: halfWidthStyle,
    },
    endDate: {
      controlType: UIControlType.date,
      placeholder: 'End date',
      'aria-label': 'Project end date',
      style: halfWidthStyle,
      className: 'right-popper',
    },
    next_inspection_date: {
      controlType: UIControlType.date,
      label: `Next Routine Inspection Date`,
      validation: {
        minDate: moment()
          .milliseconds(0)
          .seconds(0)
          .minutes(0)
          .hour(0)
          .toDate(),
      },
      style: { ...halfWidthStyle },
    },
    inspection_interval_id: {
      label: 'Routine Inspection Interval*',
      controlType: UIControlType.select,
      placeholder: 'Select Interval...',
      options: divisionInspectionIntervals,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    projectType: {
      controlType: UIControlType.select,
      label: 'Project Type*',
      options: projectTypes,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    notes: {
      label: 'Additional Notes',
      controlType: UIControlType.textarea,
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    primary_contact: {
      label: 'Primary Contact',
      controlType: UIControlType.customTagSelect,
      isMulti: false,
      openOnFocus: true,
      canSelectCustomValue: true,
      options: primaryContactUsers,
      disabled: primaryContactUsers.length === 0,
      valueKey: 'name',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
    },
    inspector_user_id: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
    document_group: {
      label: 'Regulations*',
      controlType: UIControlType.select,
      placeholder: 'Select document group...',
      options: docGroups,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
  };

  return schema;
}

export function getDocumentType(docTypes: any[]): FormSchema {
  return {
    docTypes: {
      label: 'Document Types',
      controlType: UIControlType.checkboxGroup,
      options: docTypes,
      valueKey: 'id',
      labelKey: 'name',
      showSelectAll: true,
    },
  };
}

export function getPrecipitationFields(
  precipitation_edit_lock: boolean,
  project_client: Client,
  precipitation_flag_tenant_feature: boolean,
  canShowTempest: boolean,
  isTempestSelected: boolean,
): FormSchema {
  const schema: FormSchema = {
    precipitation_threshold: {
      label: `Rain Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    snow_threshold: {
      label: `Snow Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    weather_station_type: {
      label: 'Weather Station',
      controlType: UIControlType.select,
      options: [
        { value: 'tomorrow.io', label: 'Tomorrow.io' },
        { value: 'tempest', label: 'Tempest' },
      ],
      style: {
        flex: 'none',
        flexBasis: '50%',
      },
    },
    tempest_station_id: {
      label: 'Tempest Station Id',
      controlType: UIControlType.text,
      toolTipOptions: {
        message:
          'This station ID is assigned by Tempest when you register your station on their website. It is a combination of letters and numbers that can be found in the URL of your station page. For example, if your station URL is https://tempestwx.com/station/ABCD1234, then your station ID is ABCD1234.',
      },
      validation: {
        required: isTempestSelected,
      },
      style: halfWidthStyle,
    },
    tempest_station_name: {
      label: 'Tempest Station Name',
      controlType: UIControlType.text,
      disabled: true,
      toolTipOptions: {
        message:
          'This is the name of your Tempest weather station as registered on their website. This field is for reference only and cannot be edited here.',
      },
      style: {
        flex: 'none',
        flexBasis: '50%',
      },
    },
    tempest_latitude: {
      label: 'Station Latitude',
      controlType: UIControlType.text,
      disabled: true,
      style: {
        flex: 'none',
        flexBasis: '25%',
      },
      toolTipOptions: {
        message:
          'Latitude coordinate of the Tempest weather station. This field is for reference only and cannot be edited here.',
      },
    },
    tempest_longitude: {
      label: 'Station Longitude',
      controlType: UIControlType.text,
      disabled: true,
      style: {
        flex: 'none',
        flexBasis: '25%',
      },
      toolTipOptions: {
        message:
          'Longitude coordinate of the Tempest weather station. This field is for reference only and cannot be edited here.',
      },
    },
  };

  if (
    project_client.weather_station_type !== 'tempest' ||
    project_client.is_tempest_locked ||
    !canShowTempest
  ) {
    delete schema.weather_station_type;
  }

  if (
    project_client.weather_station_type !== 'tempest' ||
    !canShowTempest ||
    !isTempestSelected
  ) {
    delete schema.tempest_station_id;
    delete schema.tempest_station_name;
    delete schema.tempest_latitude;
    delete schema.tempest_longitude;
  }

  return schema;
}

export function getSchemaInspectionCertification(
  isEnabled: boolean,
  isSameForBoth: boolean,
  selectedcertificationType: string = 'compliance',
) {
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    certification_type: {
      label: 'Certification Type',
      controlType: UIControlType.select,
      labelKey: 'label',
      valueKey: 'value',
      options: [
        { value: 'compliance', label: 'Compliance' },
        { value: 'acknowledgement', label: 'Acknowledgement' },
        { value: 'both', label: 'Both' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: '33%',
      },
    },
    no_of_days_compliance: {
      label: 'Due In (Compliance)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    no_of_days_acknowledgment: {
      label: 'Due In (Acknowledgement)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    sameForBoth: {
      label: 'Same for both',
      controlType: UIControlType.checkbox,
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
        display: 'none',
      },
    },
  };

  if (['compliance', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_compliance.validation) {
      schema.no_of_days_compliance.validation.required = true;
    }
    schema.no_of_days_compliance.disabled = false;
  }

  if (['acknowledgement', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_acknowledgment.validation) {
      schema.no_of_days_acknowledgment.validation.required = true;
    }
    schema.no_of_days_acknowledgment.disabled = false;
  }

  if (isEnabled && selectedcertificationType === 'both') {
    schema.sameForBoth.style!.display = 'block';
  }

  if (isSameForBoth && selectedcertificationType === 'both') {
    schema.no_of_days_compliance.label = 'Due In';
    if (schema.no_of_days_compliance.toolTipOptions) {
      schema.no_of_days_compliance.toolTipOptions.message =
        'Set the number of days after an inspection is submitted by which both the certifications must be signed';
    }
    delete schema.no_of_days_acknowledgment;
  }

  if (!isEnabled) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
  }

  return schema;
}

export function getSchedularFields(schemaData: any): FormSchema {
  return {
    schedule_template: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: schemaData.inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    schedule_date: {
      label: 'Schedule Date*',
      controlType: UIControlType.date,
      validation: {
        required: true,
        minDate: new Date(),
      },
    },
    schedule_type: {
      label: 'Inspection Type*',
      controlType: UIControlType.select,
      validation: { required: true },
      options: [
        { id: 1, value: 'Routine' },
        { id: 2, value: 'Routine + Post Storm' },
        { id: 3, value: 'Post Storm' },
        { id: 4, value: 'Final' },
        { id: 5, value: 'Other' },
      ],
      labelKey: 'value',
      valueKey: 'value',
    },
    schedule_inspector: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: schemaData.users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
  };
}
